import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  parseBOMFile,
  searchInternalParts,
  searchDigiKeyBatch,
  confirmBOMImport,
  type BOMWorkflowItem,
  type BOMParseResponse,
  type DigiKeyPartMatch,
  type BOMParseError,
  type BOMColumnMapping,
} from '../../services/bomService';

interface BOMImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  currentRevision?: string;
  revisions?: { revision: string; status: string }[];
  onImportComplete?: () => void;
}

type Step = 'upload' | 'preview' | 'internal-search' | 'digikey-search' | 'confirm' | 'importing' | 'complete';

const BOMImportDialog = ({
  isOpen,
  onClose,
  productId,
  currentRevision,
  revisions = [],
  onImportComplete,
}: BOMImportDialogProps) => {
  // Core state
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Parse results
  const [parseResult, setParseResult] = useState<BOMParseResponse | null>(null);
  const [items, setItems] = useState<BOMWorkflowItem[]>([]);
  const [errors, setErrors] = useState<BOMParseError[] | null>([]);
  const [columnMapping, setColumnMapping] = useState<BOMColumnMapping | null>(null);
  const [detectedHeaders, setDetectedHeaders] = useState<string[]>([]);

  // Target revision
  const [targetRevision, setTargetRevision] = useState<string>(currentRevision || 'new');
  const [newRevisionCode, setNewRevisionCode] = useState('');

  // Import result
  const [importResult, setImportResult] = useState<{
    itemsImported: number;
    newPartsCreated: number;
  } | null>(null);

  // Expanded item for details
  const [expandedItem, setExpandedItem] = useState<number | null>(null);

  // Confirmation dialog for leaving
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);

  // DigiKey OAuth state
  const [showDigiKeyAuth, setShowDigiKeyAuth] = useState(false);
  const [digiKeyAuthUrl, setDigiKeyAuthUrl] = useState<string | null>(null);
  const authWindowRef = useRef<Window | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Check if workflow has unsaved progress
  const hasUnsavedProgress = step !== 'upload' && step !== 'complete';

  // Handle browser navigation/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedProgress && isOpen) {
        e.preventDefault();
        e.returnValue = 'You have unsaved BOM import progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProgress, isOpen]);

  // File handling
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && isValidFileType(droppedFile)) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && isValidFileType(selectedFile)) {
      setFile(selectedFile);
      setError(null);
    } else if (selectedFile) {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
    }
  };

  const isValidFileType = (file: File): boolean => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    return validTypes.includes(file.type) || validExtensions.includes(extension);
  };

  // Step 1: Parse CSV
  const handleParse = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const response = await parseBOMFile(file);
      if (response.success) {
        setParseResult(response);
        setItems(response.items || []);
        setErrors(response.errors || []);
        setColumnMapping(response.column_mapping);
        setDetectedHeaders(response.detected_headers || []);
        setStep('preview');
      } else {
        setError(response.error || 'Failed to parse file');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Search internal parts
  const handleSearchInternal = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await searchInternalParts(items);
      if (response.success) {
        setItems(response.items);
        setStep('internal-search');
      } else {
        setError(response.error || 'Failed to search internal parts');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search internal parts');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Search DigiKey for unmatched (pending only, not found/matched)
  const handleSearchDigiKey = async () => {
    const unmatchedItems = items.filter(item => item.status === 'pending');
    if (unmatchedItems.length === 0) {
      setStep('confirm');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await searchDigiKeyBatch(items);
      if (response.success) {
        setItems(response.items);
        setStep('digikey-search');
      } else if (response.auth_required && response.auth_url) {
        // DigiKey auth required - show auth dialog
        setDigiKeyAuthUrl(response.auth_url);
        setShowDigiKeyAuth(true);
        setError(null); // Clear error, we're handling it with the auth dialog
      } else {
        setError(response.error || 'Failed to search DigiKey');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search DigiKey');
    } finally {
      setLoading(false);
    }
  };

  // Handle DigiKey OAuth popup
  const handleDigiKeyAuth = () => {
    if (!digiKeyAuthUrl) return;

    // Open OAuth in a popup window
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    authWindowRef.current = window.open(
      digiKeyAuthUrl,
      'DigiKey Authentication',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    // Poll to check when the popup closes
    const checkInterval = setInterval(() => {
      if (authWindowRef.current?.closed) {
        clearInterval(checkInterval);
        authWindowRef.current = null;
        setShowDigiKeyAuth(false);
        setDigiKeyAuthUrl(null);
        // Retry the DigiKey search after auth
        handleSearchDigiKey();
      }
    }, 500);
  };

  // Cancel DigiKey auth
  const cancelDigiKeyAuth = () => {
    if (authWindowRef.current) {
      authWindowRef.current.close();
      authWindowRef.current = null;
    }
    setShowDigiKeyAuth(false);
    setDigiKeyAuthUrl(null);
    // Skip DigiKey search and go to confirm
    setStep('confirm');
  };

  // Handle selecting an internal part match
  const handleSelectInternalPart = (lineNo: number, partNumber: string) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.line_no === lineNo
          ? { ...item, selected_part_no: partNumber, status: 'matched' as const }
          : item
      )
    );
  };

  // Handle selecting a DigiKey part match
  const handleSelectDigiKeyPart = (lineNo: number, match: DigiKeyPartMatch) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.line_no === lineNo
          ? { ...item, selected_digikey: match, status: 'digikey' as const }
          : item
      )
    );
  };

  // Handle marking item for manual entry
  const handleMarkManual = (lineNo: number) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.line_no === lineNo
          ? { ...item, status: 'manual' as const }
          : item
      )
    );
  };

  // Final step: Confirm and save BOM
  const handleConfirm = async () => {
    if (targetRevision === 'new' && !newRevisionCode.trim()) {
      setError('Please enter a revision code');
      return;
    }

    setLoading(true);
    setError(null);
    setStep('importing');

    try {
      const response = await confirmBOMImport({
        product_id: productId,
        revision: targetRevision !== 'new' ? targetRevision : undefined,
        new_revision: targetRevision === 'new' ? newRevisionCode.trim() : undefined,
        items: items,
        create_new_parts: true,
      });

      if (response.success) {
        setImportResult({
          itemsImported: response.items_imported,
          newPartsCreated: response.new_parts_created,
        });
        setStep('complete');
        onImportComplete?.();
      } else {
        setError(response.error || 'Import failed');
        setStep('confirm');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setStep('confirm');
    } finally {
      setLoading(false);
    }
  };

  // Request to close - check for unsaved progress first
  const requestClose = () => {
    if (hasUnsavedProgress) {
      setShowLeaveConfirm(true);
      setPendingClose(true);
    } else {
      handleClose();
    }
  };

  // Confirm leaving and close
  const confirmLeave = () => {
    setShowLeaveConfirm(false);
    setPendingClose(false);
    handleClose();
  };

  // Cancel leaving
  const cancelLeave = () => {
    setShowLeaveConfirm(false);
    setPendingClose(false);
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setItems([]);
    setErrors([]);
    setError(null);
    setImportResult(null);
    setNewRevisionCode('');
    setExpandedItem(null);
    setShowLeaveConfirm(false);
    setPendingClose(false);
    onClose();
  };

  const getStatusBadge = (status: BOMWorkflowItem['status']) => {
    const badges: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-gray-100 text-gray-800', label: 'Pending' },
      found: { color: 'bg-yellow-100 text-yellow-800', label: 'Found' },
      matched: { color: 'bg-green-100 text-green-800', label: 'Matched' },
      digikey: { color: 'bg-blue-100 text-blue-800', label: 'DigiKey' },
      manual: { color: 'bg-orange-100 text-orange-800', label: 'Manual' },
      error: { color: 'bg-red-100 text-red-800', label: 'Error' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getStepIndicator = () => {
    const steps = [
      { key: 'upload', label: 'Upload' },
      { key: 'preview', label: 'Preview' },
      { key: 'internal-search', label: 'Internal' },
      { key: 'digikey-search', label: 'DigiKey' },
      { key: 'confirm', label: 'Confirm' },
    ];

    const currentIndex = steps.findIndex(s => s.key === step);
    if (currentIndex === -1) return null;

    return (
      <div className="flex items-center justify-center space-x-2 mb-4">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              i < currentIndex ? 'bg-green-500 text-white' :
              i === currentIndex ? 'bg-blue-500 text-white' :
              'bg-gray-200 text-gray-600'
            }`}>
              {i < currentIndex ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <span className={`ml-1 text-xs ${i === currentIndex ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <div className={`w-8 h-0.5 mx-2 ${i < currentIndex ? 'bg-green-500' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render: Upload Step
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upload BOM File</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Upload a CSV or Excel file with Part Type, Description, Qty, Ref Des columns
        </p>
      </div>

      {/* File Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : file
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          className="hidden"
        />
        {file ? (
          <div className="space-y-2">
            <svg className="w-12 h-12 mx-auto text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        ) : (
          <div className="space-y-2">
            <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Drag and drop or <span className="text-blue-500">browse</span>
            </p>
            <p className="text-xs text-gray-500">Supported: CSV, XLSX, XLS</p>
          </div>
        )}
      </div>

      {/* Expected format note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-1">Expected Format</h4>
        <p className="text-xs text-blue-600 dark:text-blue-300">
          CSV with columns: Part Type (SMC, SMR, MCU, etc.), Description, Qty, Unit, Ref Des
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );

  // Render: Preview Step (after parsing)
  const renderPreviewStep = () => (
    <div className="space-y-4">
      {getStepIndicator()}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Parsed BOM</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {parseResult?.file_name} - {items.length} items parsed
          </p>
        </div>
        {columnMapping && (
          <span className={`px-2 py-1 text-xs rounded-full ${
            columnMapping.confidence >= 0.8 ? 'bg-green-100 text-green-800' :
            columnMapping.confidence >= 0.5 ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {Math.round(columnMapping.confidence * 100)}% confidence
          </span>
        )}
      </div>

      {/* Column Mapping */}
      {columnMapping && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Detected Columns</h4>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {columnMapping.part_code >= 0 && (
              <div>
                <span className="text-gray-500">Part Type:</span>{' '}
                <span className="font-medium">{detectedHeaders[columnMapping.part_code]}</span>
              </div>
            )}
            {columnMapping.description >= 0 && (
              <div>
                <span className="text-gray-500">Description:</span>{' '}
                <span className="font-medium">{detectedHeaders[columnMapping.description]}</span>
              </div>
            )}
            {columnMapping.quantity >= 0 && (
              <div>
                <span className="text-gray-500">Quantity:</span>{' '}
                <span className="font-medium">{detectedHeaders[columnMapping.quantity]}</span>
              </div>
            )}
            {columnMapping.ref_des >= 0 && (
              <div>
                <span className="text-gray-500">Ref Des:</span>{' '}
                <span className="font-medium">{detectedHeaders[columnMapping.ref_des]}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="overflow-auto max-h-72 border dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Line</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Part Type</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Description</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Qty</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Ref Des</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {items.slice(0, 30).map((item) => (
              <tr key={item.line_no} className={item.status === 'error' ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                <td className="px-3 py-2 text-gray-500">{item.line_no}</td>
                <td className="px-3 py-2">
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-mono">
                    {item.part_type}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 truncate max-w-xs" title={item.description}>
                  {item.description}
                </td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{item.qty_per} {item.unit}</td>
                <td className="px-3 py-2 text-gray-500 font-mono text-xs">{item.ref_des}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length > 30 && (
          <div className="text-center py-2 text-sm text-gray-500 bg-gray-50 dark:bg-gray-800">
            ... and {items.length - 30} more items
          </div>
        )}
      </div>

      {/* Errors */}
      {errors && errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
          <h4 className="text-sm font-medium text-red-800 dark:text-red-400 mb-2">
            {errors.length} Error{errors.length > 1 ? 's' : ''}
          </h4>
          <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
            {errors.slice(0, 5).map((err, i) => (
              <li key={i}>Line {err.line_no}: {err.message}</li>
            ))}
            {errors.length > 5 && <li>... and {errors.length - 5} more errors</li>}
          </ul>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );

  // Render: Internal Search Results Step
  const renderInternalSearchStep = () => {
    const foundCount = items.filter(i => i.status === 'found').length;
    const matchedCount = items.filter(i => i.status === 'matched').length;
    const pendingCount = items.filter(i => i.status === 'pending').length;

    return (
      <div className="space-y-4">
        {getStepIndicator()}

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Internal Parts Search</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {foundCount > 0 && <span className="text-yellow-600">{foundCount} found</span>}
              {foundCount > 0 && matchedCount > 0 && ', '}
              {matchedCount > 0 && <span className="text-green-600">{matchedCount} matched</span>}
              {(foundCount > 0 || matchedCount > 0) && pendingCount > 0 && ', '}
              {pendingCount > 0 && <span className="text-gray-600">{pendingCount} pending</span>}
            </p>
          </div>
        </div>

        {/* Items with matches */}
        <div className="overflow-auto max-h-96 space-y-2">
          {items.map((item) => (
            <div key={item.line_no} className="border dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Item header */}
              <div
                className={`flex items-center justify-between px-4 py-2 cursor-pointer ${
                  item.status === 'matched' ? 'bg-green-50 dark:bg-green-900/20' :
                  item.status === 'found' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  item.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' :
                  'bg-gray-50 dark:bg-gray-800'
                }`}
                onClick={() => setExpandedItem(expandedItem === item.line_no ? null : item.line_no)}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500 w-8">#{item.line_no}</span>
                  <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-mono">
                    {item.part_type}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                    {item.description}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {item.selected_part_no && (
                    <span className="text-xs text-green-600 font-mono">{item.selected_part_no}</span>
                  )}
                  {getStatusBadge(item.status)}
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${expandedItem === item.line_no ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded details with matches */}
              {expandedItem === item.line_no && (
                <div className="px-4 py-3 bg-white dark:bg-gray-900 border-t dark:border-gray-700">
                  {item.internal_matches && item.internal_matches.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Select a matching internal part:</p>
                      {item.internal_matches.map((match, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleSelectInternalPart(item.line_no, match.part_number)}
                          className={`p-2 border rounded cursor-pointer transition-colors ${
                            item.selected_part_no === match.part_number
                              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                                {match.part_number}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">{match.brand}</span>
                            </div>
                            <span className="text-xs text-gray-400">{Math.round(match.match_score * 100)}% match</span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{match.vendor_spec}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{match.match_reason}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No internal matches found</p>
                  )}

                  <button
                    onClick={() => handleMarkManual(item.line_no)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                  >
                    Mark for manual entry
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render: DigiKey Search Results Step
  const renderDigiKeySearchStep = () => {
    const digikeyCount = items.filter(i => i.status === 'digikey').length;
    const pendingCount = items.filter(i => i.status === 'pending').length;

    return (
      <div className="space-y-4">
        {getStepIndicator()}

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">DigiKey Search Results</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {digikeyCount} from DigiKey, {pendingCount} still pending
            </p>
          </div>
        </div>

        {/* Items with DigiKey matches */}
        <div className="overflow-auto max-h-96 space-y-2">
          {items.filter(i => i.status !== 'matched').map((item) => (
            <div key={item.line_no} className="border dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Item header */}
              <div
                className={`flex items-center justify-between px-4 py-2 cursor-pointer ${
                  item.status === 'digikey' ? 'bg-blue-50 dark:bg-blue-900/20' :
                  item.status === 'manual' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  'bg-gray-50 dark:bg-gray-800'
                }`}
                onClick={() => setExpandedItem(expandedItem === item.line_no ? null : item.line_no)}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500 w-8">#{item.line_no}</span>
                  <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-mono">
                    {item.part_type}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                    {item.description}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  {item.selected_digikey && (
                    <span className="text-xs text-blue-600 font-mono">{item.selected_digikey.mpn}</span>
                  )}
                  {getStatusBadge(item.status)}
                </div>
              </div>

              {/* Expanded details with DigiKey matches */}
              {expandedItem === item.line_no && (
                <div className="px-4 py-3 bg-white dark:bg-gray-900 border-t dark:border-gray-700">
                  {item.digikey_matches && item.digikey_matches.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Select a DigiKey part:</p>
                      {item.digikey_matches.slice(0, 5).map((match, idx) => (
                        <div
                          key={idx}
                          onClick={() => handleSelectDigiKeyPart(item.line_no, match)}
                          className={`p-2 border rounded cursor-pointer transition-colors ${
                            item.selected_digikey?.digikey_pn === match.digikey_pn
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium text-sm text-gray-900 dark:text-white">
                                {match.mpn}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">{match.manufacturer}</span>
                            </div>
                            <span className="text-xs font-medium text-green-600">
                              ${match.unit_price.toFixed(3)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                            {match.description}
                          </p>
                          <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                            <span>Stock: {match.stock.toLocaleString()}</span>
                            <span>MOQ: {match.min_order_qty}</span>
                            <span>{match.package_type}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No DigiKey matches found</p>
                  )}

                  <button
                    onClick={() => handleMarkManual(item.line_no)}
                    className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                  >
                    Enter part manually instead
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render: Confirm Step
  const renderConfirmStep = () => {
    const foundCount = items.filter(i => i.status === 'found').length;
    const matchedCount = items.filter(i => i.status === 'matched').length;
    const digikeyCount = items.filter(i => i.status === 'digikey').length;
    const manualCount = items.filter(i => i.status === 'manual').length;
    const pendingCount = items.filter(i => i.status === 'pending').length;

    return (
      <div className="space-y-4">
        {getStepIndicator()}

        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Confirm BOM Import</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Review and confirm the import
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{matchedCount}</div>
            <div className="text-xs text-green-700">Matched</div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-yellow-600">{foundCount}</div>
            <div className="text-xs text-yellow-700">Found</div>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{digikeyCount}</div>
            <div className="text-xs text-blue-700">DigiKey</div>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">{manualCount}</div>
            <div className="text-xs text-orange-700">Manual</div>
          </div>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">{pendingCount}</div>
            <div className="text-xs text-gray-700">TBD</div>
          </div>
        </div>

        {/* Target Revision */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Import to Revision</h4>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={targetRevision}
              onChange={(e) => setTargetRevision(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="new">Create new revision</option>
              {revisions
                .filter((r) => r.status !== 'released')
                .map((r) => (
                  <option key={r.revision} value={r.revision}>
                    Revision {r.revision} ({r.status})
                  </option>
                ))}
            </select>
            {targetRevision === 'new' && (
              <input
                type="text"
                value={newRevisionCode}
                onChange={(e) => setNewRevisionCode(e.target.value.toUpperCase())}
                placeholder="Revision code (e.g., B)"
                maxLength={2}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
              />
            )}
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render: Importing Step
  const renderImportingStep = () => (
    <div className="text-center py-12">
      <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
      <p className="text-gray-600 dark:text-gray-400">Importing BOM items...</p>
      <p className="text-sm text-gray-500 mt-2">Creating new part codes as needed</p>
    </div>
  );

  // Render: Complete Step
  const renderCompleteStep = () => (
    <div className="text-center py-8 space-y-4">
      <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Import Complete</h3>
        <p className="text-sm text-gray-500 mt-1">
          {importResult?.itemsImported} items imported successfully
        </p>
      </div>
      {importResult && importResult.newPartsCreated > 0 && (
        <p className="text-sm text-blue-600">
          {importResult.newPartsCreated} new part codes created
        </p>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={requestClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Leave Confirmation Dialog */}
          {showLeaveConfirm && (
            <div className="absolute inset-0 z-60 flex items-center justify-center bg-black/50 rounded-xl">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 m-4 max-w-sm">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Leave Import?</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Your progress will be lost</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  You have unsaved import progress. Are you sure you want to leave? All parsed data and matches will be discarded.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelLeave}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Continue Import
                  </button>
                  <button
                    onClick={confirmLeave}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Leave
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* DigiKey Authentication Dialog */}
          {showDigiKeyAuth && (
            <div className="absolute inset-0 z-60 flex items-center justify-center bg-black/50 rounded-xl">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 m-4 max-w-md">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">DigiKey Authentication Required</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Sign in to search DigiKey parts</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  To search for parts on DigiKey, you need to authenticate with your DigiKey account.
                  A popup window will open for you to sign in.
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> After signing in, close the popup window to continue.
                    The DigiKey search will automatically retry.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={cancelDigiKeyAuth}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Skip DigiKey
                  </button>
                  <button
                    onClick={handleDigiKeyAuth}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Sign in to DigiKey
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Import BOM from File</h2>
            <button
              onClick={requestClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-auto max-h-[calc(90vh-140px)]">
            {step === 'upload' && renderUploadStep()}
            {step === 'preview' && renderPreviewStep()}
            {step === 'internal-search' && renderInternalSearchStep()}
            {step === 'digikey-search' && renderDigiKeySearchStep()}
            {step === 'confirm' && renderConfirmStep()}
            {step === 'importing' && renderImportingStep()}
            {step === 'complete' && renderCompleteStep()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div>
              {step === 'preview' && (
                <span className="text-xs text-gray-500">
                  {items.length} items ready for search
                </span>
              )}
              {step === 'internal-search' && (
                <span className="text-xs text-gray-500">
                  {items.filter(i => i.status === 'found').length} found,{' '}
                  {items.filter(i => i.status === 'matched').length} matched,{' '}
                  {items.filter(i => i.status === 'pending').length} pending
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {step === 'upload' && (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleParse}
                    disabled={!file || loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    Parse File
                  </button>
                </>
              )}

              {step === 'preview' && (
                <>
                  <button
                    onClick={() => {
                      setStep('upload');
                      setParseResult(null);
                      setItems([]);
                      setError(null);
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSearchInternal}
                    disabled={loading || items.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    Search Internal Parts
                  </button>
                </>
              )}

              {step === 'internal-search' && (
                <>
                  <button
                    onClick={() => setStep('preview')}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep('confirm')}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Skip DigiKey
                  </button>
                  <button
                    onClick={handleSearchDigiKey}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    )}
                    Search DigiKey
                  </button>
                </>
              )}

              {step === 'digikey-search' && (
                <>
                  <button
                    onClick={() => setStep('internal-search')}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep('confirm')}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Continue to Confirm
                  </button>
                </>
              )}

              {step === 'confirm' && (
                <>
                  <button
                    onClick={() => {
                      const hasDigiKey = items.some(i => i.digikey_matches && i.digikey_matches.length > 0);
                      setStep(hasDigiKey ? 'digikey-search' : 'internal-search');
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading || (targetRevision === 'new' && !newRevisionCode.trim())}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Import BOM
                  </button>
                </>
              )}

              {step === 'complete' && (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BOMImportDialog;
